// react
import React, { useState, useEffect } from "react";

// dependencies
import PropTypes from "prop-types";

// hooks
import useWindowSize from "Hooks/useWindowSize";

// components
import Row from "./components/Row";
import TableHead from "./components/TableHead";
import TableLoading from "./components/TableLoading";

// utils
import get from "Utils/get";

// style
import css from "./Table.styl";

const Table = (props) => {
	const {
		rows,
		columns,
		subheaders,
		activeSort,
		activeSortDirection,
		onColumnClick,
		highlightColumn,
		bodyCellClass,
		headCellClass,
		isLoading,
	} = props;

	const [colWidths, setColWidths] = useState("");
	const [subheaderColWidths, setSubheaderColWidths] = useState("");
	const [highlightPosition, setHighlightPosition] = useState(null);

	const [width] = useWindowSize();
	useEffect(() => {
		// set column widths based on screen size
		const currentColWidths = columns
			.map((col) => {
				if (col.mobileViewHideColumn < width || !col.mobileViewHideColumn) {
					return get(["width"], col, "1fr");
				}
				return "";
			})
			.join(" ");
		setColWidths(currentColWidths);

		// set subheader column widths based on screen size
		const subheaderValues = Object.values(subheaders);

		const subheaderItems = [];
		subheaderValues.forEach((group) => {
			group.forEach((subheader) => {
				if (subheader.mobileViewHideColumn < width || !subheader.mobileViewHideColumn) {
					const item = get(["width"], subheader, "1fr");
					subheaderItems.push(item);
				}
			});
		});

		const subheaderWidths = subheaderItems.join(" ");
		setSubheaderColWidths(subheaderWidths);
	}, [width, columns]);

	const handleColumnClick = (columnID, nextDirection) => {
		onColumnClick(columnID, nextDirection);
	};

	const handleUpdateHighlight = (columnRef) => {
		if (!columnRef || !columnRef.current) {
			setHighlightPosition(null);
			return;
		}

		const leftRelativeToParent = columnRef.current.offsetLeft;
		const position = columnRef.current.getBoundingClientRect();

		// update position of column highlighter
		setHighlightPosition({
			width: position.width,
			left: leftRelativeToParent,
		});
	};

	return (
		<div className={css.main}>
			{highlightColumn && highlightPosition ? (
				<div
					className={css.highlightColumn}
					style={{ left: highlightPosition.left, width: highlightPosition.width }}
				/>
			) : null}

			<TableHead
				columns={columns}
				colWidths={colWidths}
				subheaders={subheaders}
				activeSort={activeSort}
				highlightColumn={highlightColumn}
				onColumnClick={handleColumnClick}
				defaultCellClassName={headCellClass}
				onUpdateHighlight={handleUpdateHighlight}
				activeSortDirection={activeSortDirection}
				currentScreenWidth={width}
			/>

			{isLoading ? (
				<TableLoading rows={rows.length > 0 ? rows.length : 20} />
			) : (
				<div testid="tableLiteBody" className={css.tableBody}>
					{rows.map((row, index) => (
						<Row
							colWidths={colWidths}
							subheaderColWidths={subheaderColWidths}
							key={get(["id"], row, index)}
							row={row}
							columns={columns}
							subheaders={subheaders}
							cellClassName={bodyCellClass}
							currentScreenWidth={width}
							hoverRowURL={get(["hoverRowURL"], row, "")}
						/>
					))}
				</div>
			)}
		</div>
	);
};

Table.propTypes = {
	isLoading: PropTypes.bool,
	onColumnClick: PropTypes.func,
	activeSort: PropTypes.string,
	activeSortDirection: PropTypes.string,
	highlightColumn: PropTypes.string,
	bodyCellClass: PropTypes.string,
	headCellClass: PropTypes.string,
	subheaders: PropTypes.object,
	rows: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
};

Table.defaultProps = {
	subheaders: {},
	activeSort: null,
	activeSortDirection: null,
	highlightColumn: null,
	bodyCellClass: null,
	headCellClass: null,
	isLoading: false,
	onColumnClick: () => {},
};

export default Table;
